// The board of one stream (docs/plans/2026-10-02-borden-ontwerp.md §12.4):
// queue lanes left of a vertical line, the workflow's columns right of it.
//
// Drag and drop is native HTML5 (no library). The page keeps the dragged card
// and the slot under the pointer in state; columns report slots while the
// pointer moves and call onDrop. Three drags exist:
//   lane   -> lane (or within one)  tasksApi.queue with afterTaskId/beforeTaskId
//                                   (422 definition_of_ready_failed -> "Move anyway?" -> force)
//   lane   -> first workflow column tasksApi.submit
//                                   (409 wip_limit_reached -> confirm -> overrideWip)
//   column -> other workflow column tasksApi.transition after a confirmation
//                                   (a running step is cancelled)
// The board data is never patched locally: after every move it is refetched,
// and the project SSE (useProjectEvents) keeps it fresh in between.

import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  DEFINITION_OF_READY_FAILED_CODE,
  WIP_LIMIT_REACHED_CODE,
  type BoardCard as BoardCardData,
  type BoardColumn as BoardColumnData,
  type DefinitionOfReadyCheck,
  type Project,
  type StreamDefinition,
  type WipLimitReachedDetails,
} from '@goblic/shared'
import { ApiError, apiErrorDetails, boardApi, streamsApi, tasksApi } from '../../lib/api-client'
import { useProjectEvents } from '../../hooks/useProjectEvents'
import { useDialogs } from '../dialogs'
import { EmptyState, ErrorNote, LoadingRows, Notice, Tabs } from '../ui'
import { sceneClass } from '../goblin-art'
import { useEdgeFade } from './use-edge-fade'
import { BoardColumn } from './board-column'
import { DoneChooser } from './done-chooser'
import {
  ORDER_LABEL,
  PULL_BLOCKED_TEXT,
  STREAM_COLOR_CLASS,
  cardsByColumn,
  describePullResult,
  dropActionFor,
  initialStateColumn,
  isLaneColumn,
  neighboursOfSlot,
  newTaskLabelOf,
  type DragInfo,
  type DropSlot,
} from './board-utils'

export interface BoardPageProps {
  project: Project
  streamKey: string
  onSelectStream: (streamKey: string) => void
  onOpenTask: (taskId: string) => void
  onNewTask: (streamKey: string) => void
}

const DOR_LABEL: Record<DefinitionOfReadyCheck, string> = {
  acceptance_criteria: 'it has no acceptance criteria',
  no_open_blockers: 'it has open blockers',
  severity_set: 'it has no severity',
}

export function BoardPage({ project, streamKey, onSelectStream, onOpenTask, onNewTask }: BoardPageProps) {
  const queryClient = useQueryClient()
  const { confirm, alert } = useDialogs()
  const events = useProjectEvents(project.id)
  const edgeFade = useEdgeFade<HTMLDivElement>(streamKey)
  const [drag, setDrag] = useState<DragInfo | null>(null)
  const [slot, setSlot] = useState<DropSlot | null>(null)
  const [busy, setBusy] = useState(false)
  const [finishing, setFinishing] = useState<{
    card: BoardCardData
    states: NonNullable<BoardColumnData['terminalStates']>
  } | null>(null)
  const [notice, setNotice] = useState<{ tone: 'success' | 'info' | 'error'; text: string } | null>(null)

  const streamsQuery = useQuery({
    queryKey: ['project', project.id, 'streams'],
    queryFn: () => streamsApi.list(project.id),
  })
  const boardQuery = useQuery({
    queryKey: ['project', project.id, 'board', streamKey],
    queryFn: () => boardApi.get(project.id, streamKey),
    refetchInterval: events.status === 'open' ? 15_000 : 4_000,
  })

  const streams = useMemo(
    () => [...(streamsQuery.data?.items ?? [])].sort((a, b) => a.position - b.position),
    [streamsQuery.data],
  )
  const board = boardQuery.data
  const stream = board?.stream
  const columns = board?.columns ?? []
  const grouped = useMemo(() => cardsByColumn(board?.cards ?? []), [board?.cards])
  const otherStreams = streams.filter((s) => s.key !== streamKey && !s.workflowMissing)
  const draggedCard = drag ? (board?.cards.find((c) => c.id === drag.taskId) ?? null) : null

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['project', project.id] }),
      queryClient.invalidateQueries({ queryKey: ['tasks'] }),
      queryClient.invalidateQueries({ queryKey: ['task'] }),
    ])
  }

  /** Runs a board action: one at a time, errors in a dialog, always refreshes. */
  async function act(action: () => Promise<void>): Promise<void> {
    setBusy(true)
    setNotice(null)
    try {
      await action()
    } catch (error) {
      await alert({
        kind: 'error',
        title: 'That didn’t work',
        message: error instanceof Error ? error.message : String(error),
      })
    } finally {
      setBusy(false)
      await refresh()
    }
  }

  /** Starts a task; a full WIP limit asks whether to go over it. */
  async function submitTask(card: BoardCardData): Promise<void> {
    try {
      await tasksApi.submit(card.id)
    } catch (error) {
      if (!(error instanceof ApiError) || error.code !== WIP_LIMIT_REACHED_CODE) throw error
      const details = apiErrorDetails<WipLimitReachedDetails>(error)
      const limit = details ? `${details.inFlight} of ${details.wipLimit} are in flight` : 'the WIP limit is reached'
      const ok = await confirm({
        kind: 'warning',
        title: 'Start over the WIP limit?',
        message: `${card.ticketKey ?? card.title}: ${limit}. Start it anyway?`,
        confirmLabel: 'Start anyway',
      })
      if (ok) await tasksApi.submit(card.id, { overrideWip: true })
    }
  }

  /** Moves a task within the queue; a failed Definition of Ready asks to force it. */
  async function queueTask(
    card: BoardCardData,
    body: { lane: NonNullable<BoardColumnData['lane']>; afterTaskId?: string | null; beforeTaskId?: string | null; unpin?: boolean },
  ): Promise<void> {
    try {
      await tasksApi.queue(card.id, body)
    } catch (error) {
      if (!(error instanceof ApiError) || error.code !== DEFINITION_OF_READY_FAILED_CODE) throw error
      const failed = apiErrorDetails<{ failed: DefinitionOfReadyCheck[] }>(error)?.failed ?? []
      const ok = await confirm({
        kind: 'warning',
        title: 'Move anyway?',
        message: `${card.ticketKey ?? card.title} isn’t ready: ${
          failed.map((f) => DOR_LABEL[f]).join(', ') || 'the Definition of Ready fails'
        }.`,
        confirmLabel: 'Move anyway',
      })
      if (ok) await tasksApi.queue(card.id, { ...body, force: true })
    }
  }

  function canDropOn(column: BoardColumnData): boolean {
    return draggedCard != null && dropActionFor(draggedCard, column, columns) != null
  }

  function handleDrop(columnId: string): void {
    const card = draggedCard
    const column = columns.find((c) => c.id === columnId)
    const currentSlot = slot
    setDrag(null)
    setSlot(null)
    if (!card || !column) return
    const action = dropActionFor(card, column, columns)
    if (!action) return

    if (action.kind === 'queue') {
      const siblings = (grouped.get(column.id) ?? []).filter((c) => c.id !== card.id)
      const index = currentSlot?.columnId === column.id ? currentSlot.index : siblings.length
      void act(() => queueTask(card, { lane: action.lane, ...neighboursOfSlot(siblings, index) }))
    } else if (action.kind === 'submit') {
      void act(() => submitTask(card))
    } else if (action.kind === 'finish') {
      // Several terminal states fold into Done: ask which one before moving.
      setFinishing({ card, states: action.states })
    } else {
      void act(async () => {
        const ok = await confirm({
          kind: 'warning',
          title: `Move to ${column.label}?`,
          message: `${card.ticketKey ?? card.title} moves from ${
            columns.find((c) => c.id === card.columnId)?.label ?? 'its step'
          } to ${column.label}. A running step is cancelled.`,
          confirmLabel: 'Move',
        })
        if (ok) await tasksApi.transition(card.id, { toState: action.toState })
      })
    }
  }

  function finishWith(stateName: string): void {
    const target = finishing
    setFinishing(null)
    if (!target) return
    void act(() => tasksApi.transition(target.card.id, { toState: stateName }).then(() => undefined))
  }

  function moveToStream(card: BoardCardData, targetKey: string): void {
    const target = streams.find((s) => s.key === targetKey)
    if (!target) return
    void act(async () => {
      const ok = await confirm({
        kind: 'warning',
        title: `Move to ${target.name}?`,
        message: `${card.ticketKey ?? card.title} goes to the queue of ${target.name}. A running step is cancelled.`,
        confirmLabel: 'Move',
      })
      if (ok) await tasksApi.moveStream(card.id, { workflowKey: target.workflowKey })
    })
  }

  async function startNext(): Promise<void> {
    await act(async () => {
      const result = await streamsApi.startNext(project.id, streamKey)
      setNotice(describePullResult(result))
    })
  }

  async function toggleAutoPull(next: boolean): Promise<void> {
    await act(async () => {
      await streamsApi.update(project.id, streamKey, { autoPull: next })
    })
  }

  const lanes = columns.filter(isLaneColumn)
  const flow = columns.filter((c) => !isLaneColumn(c))
  const color = stream ? STREAM_COLOR_CLASS[stream.color] : null

  const renderColumn = (column: BoardColumnData) =>
    stream ? (
      <BoardColumn
        key={column.id}
        column={column}
        cards={grouped.get(column.id) ?? []}
        stream={stream}
        otherStreams={otherStreams}
        draggedId={drag?.taskId ?? null}
        canDrop={canDropOn(column)}
        slot={slot}
        busy={busy}
        onSlot={setSlot}
        onDrop={handleDrop}
        onDragStart={(card) => setDrag({ taskId: card.id, fromColumnId: card.columnId })}
        onDragEnd={() => {
          setDrag(null)
          setSlot(null)
        }}
        onOpen={(card) => onOpenTask(card.id)}
        onStartNow={(card) => void act(() => submitTask(card))}
        onMoveStream={moveToStream}
        onArchive={(card) =>
          void act(async () => {
            await tasksApi.archive(card.id)
          })
        }
        onUnpin={(card) => card.lane && void act(() => queueTask(card, { lane: card.lane!, unpin: true }))}
      />
    ) : null

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <h1 className="font-display text-xl font-semibold tracking-[-0.01em] text-ink">
            {project.name} <span className="font-mono text-base font-normal text-ink-3">{project.key}</span>
          </h1>
        </div>
        {stream ? (
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className="btn-secondary" disabled={busy} onClick={() => void startNext()}>
              Start next
            </button>
            <button type="button" className="btn-primary" onClick={() => onNewTask(stream.key)}>
              {newTaskLabelOf(stream)}
            </button>
          </div>
        ) : null}
      </div>

      {streams.length > 0 ? (
        <Tabs
          label="Streams"
          value={streamKey}
          onChange={onSelectStream}
          options={streams.map((s) => ({ value: s.key, label: s.name }))}
        />
      ) : null}

      {streamsQuery.isSuccess && streams.length === 0 ? (
        <div className="panel">
          <EmptyState title="No boards yet" art="sorting">
            Each workflow of this project becomes a stream with its own board. Add a workflow in the project
            settings to get one.
          </EmptyState>
        </div>
      ) : null}
      {boardQuery.isLoading ? <LoadingRows label="Loading the board…" /> : null}
      {boardQuery.isError ? <ErrorNote error={boardQuery.error} what="the board" /> : null}

      {board && stream ? (
        <>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-base text-ink-2">
            <span className="inline-flex items-center gap-1.5">
              <span className={`h-2 w-2 rounded-full ${color?.dot ?? ''}`} aria-hidden />
              In flight{' '}
              <strong className="tabular-nums text-ink">
                {board.stats.inFlight} / {board.stats.wipLimit ?? '∞'}
              </strong>
            </span>
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                role="switch"
                className="switch"
                checked={stream.autoPull}
                disabled={busy}
                onChange={(event) => void toggleAutoPull(event.target.checked)}
              />
              Auto-pull
            </label>
            <span>
              Order: <span className="text-ink">{ORDER_LABEL[stream.order]}</span>
              {stream.expedite ? <span className="text-ink-3"> · S1 skips the queue</span> : null}
            </span>
            {board.stats.blocked > 0 ? <span className="text-danger">{board.stats.blocked} blocked</span> : null}
            {stream.paused ? <span className="rounded-full bg-signal-soft px-2 text-sm text-signal">paused</span> : null}
          </div>

          {board.pullBlockedReason ? (
            <Notice tone={board.pullBlockedReason === 'auto_pull_off' ? 'info' : 'signal'}>
              {PULL_BLOCKED_TEXT[board.pullBlockedReason]}
            </Notice>
          ) : null}
          {notice ? (
            <Notice tone={notice.tone} onDismiss={() => setNotice(null)}>
              {notice.text}
            </Notice>
          ) : null}
          {stream.workflowMissing ? (
            <Notice tone="signal">
              The workflow of this stream no longer exists, so only the queue and Done are shown. Remove the stream in
              the project’s stream settings once it is empty.
            </Notice>
          ) : null}

          {/* Fills the rest of the viewport; each column scrolls its own cards. The scene sits on this
              non-scrolling wrapper so it stays put while the columns scroll sideways over it. */}
          {/* Bleeds to both edges of the main area (sidebar on the left, page edge on the right); the
              padding keeps the first column under the header, and the sides with more board fade out
              inside that padding (useEdgeFade). */}
          <div className={`-mx-4 flex min-h-0 flex-1 sm:-mx-6 ${sceneClass('board')}`}>
            <div
              ref={edgeFade.ref}
              style={edgeFade.style}
              className="flex min-h-0 min-w-0 flex-1 snap-x items-stretch gap-3 overflow-x-auto overflow-y-hidden px-4 pb-3 sm:px-6"
            >
              {lanes.map(renderColumn)}
              {lanes.length > 0 && flow.length > 0 ? (
                <div
                  className="mx-1 w-px shrink-0 self-stretch bg-line-strong"
                  role="separator"
                  aria-orientation="vertical"
                  title="Right of this line a task counts as started"
                />
              ) : null}
              {flow.map(renderColumn)}
            </div>
          </div>
        </>
      ) : null}
      {finishing ? (
        <DoneChooser
          taskLabel={finishing.card.ticketKey ?? finishing.card.title}
          states={finishing.states}
          onChoose={finishWith}
          onCancel={() => setFinishing(null)}
        />
      ) : null}
    </div>
  )
}
